/**
 * cURL code generator
 */

import { formatAnnotations } from "../../annotations";
import {
  AnnotationFormat,
  ChatCompletionRequest,
  ChatMessage,
  MultiModalContent,
  VisionConfig,
  reasoningEffortForThinking,
} from "../../apiContracts/chatCompletions";
import { DEMO_MAX_TOKENS } from "../../constants";
import { ContentItem, ExportCodeConfig, OutputFormat } from "./types";

/**
 * Check if annotations have any content
 */
function hasAnnotations(annotations?: { points: unknown[]; boundingBoxes: unknown[] }): boolean {
  return !!annotations && (annotations.points.length > 0 || annotations.boundingBoxes.length > 0);
}

/**
 * Process content items, converting annotations to text format for the API.
 */
function processContentForApi(items: ContentItem[]): MultiModalContent[] {
  const result: MultiModalContent[] = [];

  for (const item of items) {
    if (item.type === "text") {
      result.push(item);
    } else if (item.type === "image") {
      result.push({ type: "image_url", image_url: { url: item.url } });

      if ("annotations" in item && hasAnnotations(item.annotations)) {
        result.push({ type: "text", text: formatAnnotations(item.annotations!) });
      }
    } else if (item.type === "video") {
      result.push({ type: "video_url", video_url: { url: item.url } });

      if ("annotations" in item && hasAnnotations(item.annotations)) {
        result.push({ type: "text", text: formatAnnotations(item.annotations!) });
      }
    } else if (item.type === "audio") {
      result.push({ type: "audio_url", audio_url: { url: item.url } });
    }
  }

  return result;
}

/**
 * Map the export-side output format to the API's annotation_format enum.
 * Returns null for text/parsing output where no spatial annotation is emitted.
 */
function mapOutputFormatToAnnotationFormat(format: OutputFormat): AnnotationFormat | null {
  switch (format) {
    case "POINT":
      return "point";
    case "BOX":
      return "box";
    case "POLYGON":
      return "polygon";
    case "CLIP":
      return "clip";
    case "TEXT":
    case "PARSING":
    default:
      return null;
  }
}

/**
 * Build the Perceptron `vision_config` object from the export configuration, or
 * undefined when it would be empty. Reasoning is not in it: it is the request's
 * top-level `reasoning_effort`.
 *
 * `annotation_format` is omitted when it would be a no-op: the gateway treats
 * `annotation_format: null|absent` identically
 * (`chat_completions_request_conversion.rs`).
 */
function buildVisionConfig(config: ExportCodeConfig): VisionConfig | undefined {
  const visionConfig: VisionConfig = {};
  const annotationFormat = mapOutputFormatToAnnotationFormat(config.outputFormat);
  if (annotationFormat !== null) {
    visionConfig.annotation_format = annotationFormat;
  }
  if (config.enableAudioInVideo) {
    visionConfig.enable_audio_in_video = true;
  }
  return Object.keys(visionConfig).length > 0 ? visionConfig : undefined;
}

/**
 * Build the request body object for API calls. Reasoning is the top-level `reasoning_effort` (a tier
 * when on, `none` when off); output format and audio travel in `body.vision_config` when set. Nothing is
 * injected into the messages, and the export asks for no tools.
 */
function buildRequestBody(config: ExportCodeConfig): ChatCompletionRequest {
  const messages: ChatMessage[] = [];

  const systemContent = config.systemContent ? processContentForApi(config.systemContent) : [];
  if (systemContent.length > 0) {
    messages.push({
      role: "system",
      content: systemContent,
    });
  }

  messages.push({
    role: "user",
    content: processContentForApi(config.userContent),
  });

  const body: ChatCompletionRequest = {
    model: config.model,
    messages,
    max_completion_tokens: DEMO_MAX_TOKENS,
  };

  if (config.temperature !== undefined) {
    body.temperature = config.temperature;
  }

  body.reasoning_effort = reasoningEffortForThinking(config.enableReasoning);
  const visionConfig = buildVisionConfig(config);
  if (visionConfig) {
    body.vision_config = visionConfig;
  }

  return body;
}

/**
 * Generate a curl command for the API call
 */
export function generateCurl(config: ExportCodeConfig): string {
  const body = buildRequestBody(config);
  const jsonBody = JSON.stringify(body, null, 2);

  // Escape single quotes for shell safety: ' becomes '\''
  const escapedJson = jsonBody.replace(/'/g, "'\\''");

  const authHeader = config.apiKey ? `\n  -H "Authorization: Bearer ${config.apiKey}" \\` : "";

  return `curl -X POST "${config.apiUrl}" \\
  -H "Content-Type: application/json" \\${authHeader}
  -d '${escapedJson}'`;
}
